import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ElementaryMissionPage from "./ElementaryMissionPage";
import LemonBotJr from "@/components/LemonBotJr";
import MissionVideo from "@/components/MissionVideo";
import MissionCompleteOverlay from "@/components/MissionCompleteOverlay";
import {
  loadJrState,
  saveJrState,
  markMissionComplete,
  JR_PITCH_CHIPS,
} from "@/utils/jrDemoState";
import { getMissionVideo } from "@/utils/jrMissionMedia";
import { PencilLine, Sparkles } from "lucide-react";

const STARTER = "My business helps ______ by ______.";

const EXAMPLES = [
  "My business helps students stay ready to learn by giving them pencils when they need one.",
  "My business helps classmates feel encouraged by sharing kindness cards.",
];

function joinGuided({ who, how, why }) {
  const w = (who || "").trim();
  const h = (how || "").trim();
  const wh = (why || "").trim();
  if (!w || !h) return "";
  let s = `My business helps ${w} by ${h}`;
  if (!s.endsWith(".")) s += ".";
  if (wh) {
    s = s.replace(/\.$/, "");
    s += `, because ${wh}.`;
  }
  return s;
}

function parseGuided(pitch) {
  if (!pitch) return { who: "", how: "", why: "" };
  const m = pitch.match(/^My business helps (.+?) by (.+?)(?:,\s*because (.+?))?\.?$/i);
  if (!m) return { who: "", how: "", why: "" };
  return { who: (m[1] || "").trim(), how: (m[2] || "").trim(), why: (m[3] || "").trim() };
}

/**
 * BlankPicker — one row of chips for who/how/why, with a "Write my own"
 * toggle that flips that blank into a free-text input.
 */
function BlankPicker({ label, optional, chips, value, onChange, testIdBase, color = "yellow" }) {
  const isCustom = !!value && !chips.includes(value);
  const [mode, setMode] = useState(isCustom ? "custom" : "chips");

  const colorMap = {
    yellow: { sel: "bg-yellow-500 text-white", chip: "bg-white text-yellow-800 ring-yellow-200 hover:ring-yellow-400" },
    sky:    { sel: "bg-sky-500 text-white",    chip: "bg-white text-sky-800 ring-sky-200 hover:ring-sky-400" },
    rose:   { sel: "bg-rose-500 text-white",   chip: "bg-white text-rose-800 ring-rose-200 hover:ring-rose-400" },
  };
  const c = colorMap[color] || colorMap.yellow;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-sm font-bold text-gray-900">
          {label}{" "}
          {optional && <span className="text-gray-400 font-normal">(optional)</span>}
        </label>
        <button
          type="button"
          onClick={() => {
            if (mode === "chips") {
              setMode("custom");
              if (chips.includes(value)) onChange("");
            } else {
              setMode("chips");
              onChange("");
            }
          }}
          className="text-xs font-bold text-yellow-700 hover:text-yellow-900 inline-flex items-center gap-1"
          data-testid={`${testIdBase}-toggle-mode`}
        >
          <PencilLine className="w-3 h-3" />
          {mode === "chips" ? "Write my own" : "Pick a chip"}
        </button>
      </div>

      {mode === "chips" ? (
        <div className="flex flex-wrap gap-2" data-testid={`${testIdBase}-chips`}>
          {chips.map((chip) => {
            const selected = value === chip;
            return (
              <button
                key={chip}
                type="button"
                onClick={() => onChange(selected ? "" : chip)}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all ring-2 ${
                  selected ? `${c.sel} shadow ring-transparent` : `${c.chip}`
                }`}
                data-testid={`${testIdBase}-chip-${chip.replace(/\s+/g, "-")}`}
                aria-pressed={selected}
              >
                {chip}
              </button>
            );
          })}
        </div>
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={120}
          placeholder="Write your own…"
          className="w-full px-4 py-3 border-2 border-yellow-200 rounded-2xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
          data-testid={`${testIdBase}-custom-input`}
          autoFocus
        />
      )}
    </div>
  );
}

export default function Mission8() {
  const navigate = useNavigate();
  const initialPitch = loadJrState().pitch || "";
  const initialGuided = parseGuided(initialPitch);
  const [mode, setMode] = useState(
    initialPitch && !initialGuided.who ? "freetext" : "guided"
  );
  const [who, setWho] = useState(initialGuided.who);
  const [how, setHow] = useState(initialGuided.how);
  const [why, setWhy] = useState(initialGuided.why);
  const [pitchFree, setPitchFree] = useState(initialPitch);
  const [showOverlay, setShowOverlay] = useState(false);
  const video = getMissionVideo(8);

  const guidedPitch = joinGuided({ who, how, why });
  const effectivePitch = mode === "guided" ? guidedPitch : pitchFree.trim();
  const canContinue = effectivePitch.length >= 10;

  const onContinue = () => {
    if (!canContinue) return;
    const isCanned = EXAMPLES.includes(effectivePitch) || effectivePitch === STARTER;
    saveJrState({ pitch: effectivePitch });
    markMissionComplete(8, { customized: !isCanned });
    setShowOverlay(true);
  };

  const applyStarter = () => {
    setMode("freetext");
    setPitchFree(STARTER);
  };
  const applyExample = (ex) => {
    setMode("freetext");
    setPitchFree(ex);
  };

  return (
    <ElementaryMissionPage
      missionNumber={8}
      title="Pitch Day"
      lesson="A pitch is a short, exciting talk about your business. Practice it out loud!"
      canContinue={canContinue}
      onContinue={onContinue}
      continueLabel="Save & Back to CEO City"
      testId="elementary-mission-8-page"
    >
      {video && (
        <MissionVideo url={video.url} title={video.title} testId="mission-8-video" />
      )}

      <div className="mb-5">
        <LemonBotJr
          tone="green"
          greeting="Let's build your 30-second pitch! Tap a chip for each part — it's like a Junior CEO mad-lib."
          prompts={[
            "Pick chips, or tap 'Write my own' for any blank.",
            "Watch your pitch come together below.",
          ]}
          compact
        />
      </div>

      {/* Mode toggle */}
      <div className="mb-5 flex items-center gap-2 text-xs">
        <button
          type="button"
          onClick={() => setMode("guided")}
          className={`px-3 py-1.5 rounded-full font-bold transition-colors ${
            mode === "guided"
              ? "bg-yellow-500 text-white"
              : "bg-white text-gray-700 ring-1 ring-yellow-200 hover:ring-yellow-400"
          }`}
          data-testid="mission-8-mode-guided"
        >
          Guided fill-in
        </button>
        <button
          type="button"
          onClick={() => setMode("freetext")}
          className={`px-3 py-1.5 rounded-full font-bold transition-colors ${
            mode === "freetext"
              ? "bg-yellow-500 text-white"
              : "bg-white text-gray-700 ring-1 ring-yellow-200 hover:ring-yellow-400"
          }`}
          data-testid="mission-8-mode-freetext"
        >
          Free write
        </button>
      </div>

      {mode === "guided" ? (
        <div className="space-y-5" data-testid="mission-8-guided-block">
          <BlankPicker
            label="Who does your business help?"
            chips={JR_PITCH_CHIPS.who}
            value={who}
            onChange={setWho}
            testIdBase="mission-8-guided-who"
            color="yellow"
          />
          <BlankPicker
            label="How does your business help?"
            chips={JR_PITCH_CHIPS.how}
            value={how}
            onChange={setHow}
            testIdBase="mission-8-guided-how"
            color="sky"
          />
          <BlankPicker
            label="Why does this matter?"
            optional
            chips={JR_PITCH_CHIPS.why}
            value={why}
            onChange={setWhy}
            testIdBase="mission-8-guided-why"
            color="rose"
          />

          {/* Live pitch preview */}
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 ring-2 ring-yellow-300 rounded-2xl p-4 flex items-start gap-3">
            <div
              className="bg-yellow-500 text-white w-9 h-9 rounded-full flex items-center justify-center text-lg shrink-0 shadow"
              aria-hidden="true"
            >
              📣
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-yellow-800 uppercase tracking-wide mb-1">
                Your pitch so far
              </p>
              <p
                className="text-gray-900 italic leading-relaxed min-h-[1.5rem] text-base"
                data-testid="mission-8-guided-preview"
              >
                {guidedPitch || (
                  <span className="not-italic text-gray-400">
                    Tap a chip above to start your pitch…
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-yellow-50 ring-1 ring-yellow-200 rounded-2xl p-4 mb-5 flex items-start gap-3">
            <div className="bg-yellow-500 text-white w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0">
              📣
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-yellow-800 uppercase tracking-wide mb-1">
                Pitch Starter
              </p>
              <p className="text-gray-800 italic leading-relaxed">"{STARTER}"</p>
            </div>
            <button
              type="button"
              onClick={applyStarter}
              className="shrink-0 bg-white hover:bg-yellow-50 ring-1 ring-yellow-300 hover:ring-yellow-500 text-yellow-800 text-xs font-bold px-3 py-1.5 rounded-full inline-flex items-center gap-1 transition-all"
              data-testid="mission-8-use-starter"
            >
              <PencilLine className="w-3 h-3" />
              Use starter
            </button>
          </div>

          <label className="block text-base font-semibold text-gray-900 mb-3">
            Write your 30-second Junior CEO pitch.
          </label>
          <textarea
            value={pitchFree}
            onChange={(e) => setPitchFree(e.target.value)}
            rows={5}
            maxLength={500}
            placeholder="My pitch is…"
            className="w-full px-4 py-3 text-lg border-2 border-yellow-200 rounded-2xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none resize-none"
            data-testid="mission-8-pitch-input"
          />
          <p className="mt-2 text-xs text-gray-500">{pitchFree.length}/500</p>
        </>
      )}

      <div className="mt-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-yellow-500" />
          <p className="text-xs font-bold text-yellow-700 uppercase tracking-wide">
            Need ideas? Tap an example
          </p>
        </div>
        <div className="space-y-2">
          {EXAMPLES.map((ex, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => applyExample(ex)}
              className="w-full text-left bg-white hover:bg-yellow-50 text-gray-800 text-sm px-4 py-3 rounded-2xl ring-1 ring-yellow-200 hover:ring-yellow-400 transition-all"
              data-testid={`mission-8-example-${idx}`}
            >
              "{ex}"
            </button>
          ))}
        </div>
      </div>

      {showOverlay && (
        <MissionCompleteOverlay
          missionNumber={8}
          missionTitle="Pitch Day"
          message="That's a real pitch! Practice it out loud — Junior CEOs sound proud and clear."
          continueLabel="Back to CEO City"
          onContinue={() => navigate("/elementary/demo")}
        />
      )}
    </ElementaryMissionPage>
  );
}
